import React, { useEffect, useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import {
  fetchSubmittedPortfolio,
  portfolioHasVisibleFacilities,
  type NiPortfolio,
} from "@services/networkIntelligence";
import FacilityNetworkPageShell from "../facilityNetwork/FacilityNetworkPageShell";
import { FN } from "../facilityNetwork/facilityNetworkTheme";
import NetworkIntelligenceLoader from "./NetworkIntelligenceLoader";
import NetworkIntelligenceUpload from "./NetworkIntelligenceUpload";

function formatLiveDate(value: string | null | undefined): string {
  if (!value) return "—";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "—";
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(parsed);
}

export default function NetworkIntelligenceManage() {
  const navigate = useNavigate();
  const [portfolio, setPortfolio] = useState<NiPortfolio | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    fetchSubmittedPortfolio()
      .then((next) => {
        if (!cancelled) setPortfolio(next);
      })
      .catch(() => {
        if (!cancelled) setPortfolio(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const rows = (portfolio?.rows ?? []).filter((row) => row.review_status !== "deleted");
  const visible = portfolioHasVisibleFacilities(portfolio) ? rows : [];

  return (
    <FacilityNetworkPageShell>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2, maxWidth: 880 }}>
        <Typography sx={{ fontFamily: FN.fontFamily, fontSize: 12, color: FN.figmaAccent }}>
          <Button
            onClick={() =>
              navigate("/dashboard/network/intel", {
                state: { currentView: "network-intelligence" },
              })
            }
            sx={{
              p: 0,
              minWidth: 0,
              textTransform: "none",
              fontFamily: FN.fontFamily,
              fontSize: 12,
              color: FN.primary,
            }}
          >
            Intelligence
          </Button>
          {" › Manage portfolio"}
        </Typography>
        <Typography
          sx={{
            fontFamily: FN.fontFamily,
            fontSize: FN.figmaTitleSize,
            fontWeight: 600,
            color: FN.title,
            lineHeight: "32px",
          }}
        >
          Manage your Intelligence portfolio
        </Typography>
        {loading ? <NetworkIntelligenceLoader /> : null}
        {!loading && visible.length === 0 ? (
          <Typography sx={{ fontFamily: FN.fontFamily, fontSize: 14, color: FN.subtext }}>
            No submitted facilities yet. Upload a list below to start review.
          </Typography>
        ) : null}
        {!loading && visible.length > 0 ? (
          <Box
            sx={{
              border: `1px solid ${FN.figmaCardBorder}`,
              borderRadius: FN.figmaCardRadius,
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1.4fr 1fr 0.8fr",
                gap: 1,
                px: 2,
                py: 1,
                bgcolor: "#f3fdff",
              }}
            >
              {["Facility", "Intent", "Submitted"].map((label) => (
                <Typography
                  key={label}
                  sx={{ fontFamily: FN.fontFamily, fontSize: 11, fontWeight: 600, color: FN.figmaAccent }}
                >
                  {label}
                </Typography>
              ))}
            </Box>
            {visible.map((row) => (
              <Box
                key={row.row_number}
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1.4fr 1fr 0.8fr",
                  gap: 1,
                  px: 2,
                  py: 1.25,
                  borderTop: `1px solid ${FN.figmaCardBorder}`,
                }}
              >
                <Box>
                  <Typography sx={{ fontFamily: FN.fontFamily, fontSize: 13, fontWeight: 600, color: FN.text }}>
                    {row.facility_name}
                  </Typography>
                  <Typography sx={{ fontFamily: FN.fontFamily, fontSize: 12, color: FN.figmaAccent }}>
                    {row.address}
                  </Typography>
                </Box>
                <Typography sx={{ fontFamily: FN.fontFamily, fontSize: 13, color: FN.text }}>{row.intent}</Typography>
                <Typography sx={{ fontFamily: FN.fontFamily, fontSize: 13, color: FN.text }}>
                  {formatLiveDate(row.submitted_at || portfolio?.submitted_at)}
                </Typography>
              </Box>
            ))}
          </Box>
        ) : null}
        <Typography sx={{ fontFamily: FN.fontFamily, fontSize: 16, fontWeight: 600, color: FN.title }}>
          Add more facilities
        </Typography>
        <NetworkIntelligenceUpload />
      </Box>
    </FacilityNetworkPageShell>
  );
}
